'use client';
import React, { useState } from 'react';
import { Formik, Form } from 'formik';
import { DialogHeader } from '@src/v3/components/coreUI/dialog';
import TextField from '@src/v3/components/coreUI/textField';
import Button from '@src/v3/components/coreUI/button';
import Icon from '@src/v3/components/coreUI/icon';
import GradientPanelSvg from '@src/v3/assets/svg/gradientPanel';
import {
  RegisterStepProps,
  RegisterFormValues,
  UserType,
} from '@src/v3/interfaces/components/registerModal';
import { updateEmailValidationSchema } from '@src/v3/schemas/components/register/index';

const UpdateEmailStep: React.FC<RegisterStepProps> = ({
  onUpdateEmailSubmit,
  isUpdateEmailLoading = false,
  onClose,
  // eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars
  userDetailsData: _userDetailsData,
  email = '',
}) => {
  const [errorMessage, setErrorMessage] = useState<string>('');

  const handleSubmit = async (
    values: RegisterFormValues,
    // eslint-disable-next-line no-unused-vars
    { setSubmitting }: { setSubmitting: (_isSubmitting: boolean) => void }
  ) => {
    if (values.email === email) {
      onClose?.();
      return;
    }
    try {
      await onUpdateEmailSubmit?.(values);
    } catch (error) {
      const errorMsg =
        (
          error as {
            response?: { data?: { message?: string } };
            message?: string;
          }
        )?.response?.data?.message ||
        (error as { message?: string })?.message ||
        'Please fill in all required fields';
      if ((error as { status?: number })?.status === 400) {
        setErrorMessage('This email is already registered.');
      } else {
        setErrorMessage(errorMsg);
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleEmailChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    // eslint-disable-next-line no-unused-vars
    handleChange: (e: React.ChangeEvent<HTMLInputElement>) => void
  ) => {
    handleChange(e);
    if (errorMessage) {
      setErrorMessage('');
    }
  };

  return (
    <Formik
      initialValues={{
        phone: '',
        code: '',
        userType: 'renter' as UserType,
        email: '',
      }}
      validationSchema={updateEmailValidationSchema}
      onSubmit={handleSubmit}
    >
      {({
        values,
        errors,
        touched,
        handleChange,
        handleBlur,
        isSubmitting,
      }) => (
        <div className="h-full flex flex-col md:flex-row">
          <div className="md:w-[686px] w-full h-auto md:h-[790px] self-stretch px-2.5 md:px-2.5 pt-2.5 md:pt-2.5 pb-8 bg-white rounded-none md:rounded-l-2xl inline-flex flex-col justify-between items-start">
            <Form className="h-full flex flex-col w-full">
              <DialogHeader />
              <div className="self-stretch flex flex-col justify-start items-start gap-2.5 flex-1">
                <div className="self-stretch h-auto md:h-32 px-4 md:px-8 pt-7 md:pt-7 pb-5 bg-white flex flex-col justify-center items-start gap-3">
                  <div className="self-stretch flex flex-col justify-start items-start gap-4">
                    <div className="self-stretch inline-flex justify-start items-center gap-2.5">
                      <div className="flex-1 justify-start text-gray-1000 text-xl md:text-2xl font-medium font-geist leading-7 md:leading-9">
                        Update your email
                      </div>
                      <span
                        onClick={onClose}
                        className="p-2 cursor-pointer rounded-full transition-colors"
                      >
                        <Icon
                          iconName="crossicon"
                          iconProps={{ className: 'w-5 h-5 text-gray-600' }}
                        />
                      </span>
                    </div>
                  </div>
                </div>

                <div className="self-stretch px-4 md:px-8 py-2.5 flex flex-col justify-start items-end gap-6 md:gap-8">
                  <div className="self-stretch flex flex-col justify-start items-start gap-6">
                    <div className="self-stretch flex flex-col justify-start items-start gap-2">
                      <div className="self-stretch justify-start text-gray-1000 text-sm font-normal font-geist leading-6">
                        Email
                      </div>
                      <TextField
                        value={values.email}
                        error={touched.email && !!errors.email}
                        errorMessage={touched.email ? errors.email : ''}
                        onChange={(e) => handleEmailChange(e, handleChange)}
                        onBlur={handleBlur}
                        name="email"
                        placeholder="Enter your email"
                        size="md"
                        variant="light"
                        type="email"
                        disabled={isUpdateEmailLoading || isSubmitting}
                        inputClassName="!text-gray-1000 !text-sm leading-5"
                        showLabel={false}
                      />
                      {/* Error Message - Desktop */}
                      {errorMessage && (
                        <div className="self-stretch inline-flex justify-start items-start gap-2">
                          <Icon
                            iconName="warningCircle"
                            iconProps={{
                              className: 'w-5 h-5 flex-shrink-0 mt-0.5',
                            }}
                          />
                          <span className="font-geist font-normal text-[#E5484D] text-[14px] leading-[24px] tracking-[-0.14px]">
                            {errorMessage}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>

                  <Button
                    type="submit"
                    variant="primary"
                    size="xl"
                    className="self-stretch h-12 px-4 py-3 bg-focus rounded-[36px] inline-flex justify-center items-center gap-2"
                    disabled={isUpdateEmailLoading || isSubmitting}
                    isLoading={isUpdateEmailLoading || isSubmitting}
                    loadingType="dots"
                  >
                    <div className="justify-start text-white text-base font-medium font-geist leading-7">
                      Continue
                    </div>
                    <Icon
                      iconName="arrowRight"
                      iconProps={{ className: 'size-5 text-white' }}
                    />
                  </Button>
                </div>
              </div>

              <div className="self-stretch px-4 md:px-8 opacity-0 inline-flex justify-start items-center gap-24">
                <div className="justify-start text-gray-1000 text-sm font-normal font-geist leading-6">
                  Go Back
                </div>
              </div>
            </Form>
          </div>
          <div className="hidden md:flex relative flex-col p-2.5 items-start gap-2.5 self-stretch shrink-0 rounded-r-2xl bg-background-200 overflow-hidden w-[550px] h-[790px]">
            <GradientPanelSvg
              className="absolute inset-0 w-full h-full"
              preserveAspectRatio="none"
            />
          </div>
        </div>
      )}
    </Formik>
  );
};

export default UpdateEmailStep;
